import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import type { FormInstance, FormProps } from "antd";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CameraOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import "./modern-styles.css";
import dayjs from "dayjs";
import { AddPatientFormProps } from "../../props/AddPatientProps";
import "./index.css";
import { FieldType, initialValues } from "../../props/PatientDetailsProps";
import PatientReferral from "./Referrals/PatientReferral";
import ConfirmModal from "../../utils/confirmModal";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ConfirmModalUploadDoc from "../../utils/confirmModalUploadDoc";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { useWatch } from "antd/es/form/Form";
import PatientConfirmationModal from "../../modals/patientConfirmationModal";
import PatientDocumentUpload from "./PatientDocumentUpload";
import "./responsive-styles.css";
import { Card } from "antd";
import RightSidePreviewModal from "../../modals/RightSidePreviewModal";
import ReferralCard from "./Referrals/ReferralCard";
import ReferralEditModal from "./ReferralEditModal";

dayjs.extend(customParseFormat);

const { Text } = Typography;

type ComponentDetail = {
  id: number;
  name: string;
  validUpto: string; // Adjust type as needed
  remarks?: string;
  maxQty?: string;
  referralType: string;
};

type Department = {
  name: string;
  description: string;
};

const AddNewPatientForm: FC<AddPatientFormProps> = (props) => {
  const formatDates: any = (obj: any) => {
    if (!obj || typeof obj !== "object") return obj;

    if (dayjs.isDayjs(obj)) {
      return obj.format("DD-MM-YYYY");
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => formatDates(item));
    }

    const formatted: any = {};
    Object.keys(obj)?.forEach((key) => {
      formatted[key] = formatDates(obj[key]);
    });

    return formatted;
  };

  const { Option } = Select;
  const [patientFormData, setPatientFormData] = useState<any | null>(null);
  const cardCategoryList = ["General", "Semi Private", "Private"];
  const [patientDocType, setPatientDocType] = useState<string[]>([]);
  const { patients, scheme, hospital } = useSelector(
    (state: RootState) => state,
  );
  const [form] = Form.useForm();
  const [services, setServices] = useState<ComponentDetail[]>([]);
  const [servicesManually, setServicesManually] = useState<ComponentDetail[]>(
    [],
  );
  const [isEditIndexCard, setIsEditIndexCard] = useState<boolean>(false);
  const [isEditCard, setIsEditCard] = useState<boolean>(false);
  const [isEditReferral, setIsEditReferral] = useState<boolean>(false);

  const dateFormat = "DD-MM-YYYY HH:mm:ss";
  const [previewDocument, setPreviewDocument] = useState<boolean>(false);
  const [documentUrl, setDocumentUrl] = useState<any>(null);

  const aadharStatusOptions = [
    {
      label: "Patient does not have an Aadhaar Number",
      value: "Patient does not have an Aadhaar Number",
    },
    {
      label: "Patient has an Aadhaar Number",
      value: "Patient has an Aadhaar Number",
    },
    {
      label: "Patient is NDG / J&K / NE State hence exempted",
      value: "Patient is NDG / J&K / NE State hence exempted",
    },
  ];

  const aadharSelectedValue = Form.useWatch("aadharStatus", form);

  const [age, setAge] = useState<number | null>(null);
  const [documentTypeLabelName, setDocumentTypeLabelName] =
    useState<string>("");

  const [isFetchButtonDisabled, setIsFetchButtonDisabled] = useState(true);
  const [key, setKey] = useState<string>("");
  const [referralData, setReferralData] = useState<any>(null);
  const [isCardUploaded, setIsCardUploaded] = useState<boolean>(false);
  const [cardCategory, setCardCategory] = useState<string | null>(null);
  const [cardBeneficiaryId, setCardBeneficiaryId] = useState<string | null>(
    null,
  );

  const selectedCaseType = Form.useWatch("caseTypes", form);

  function convertDateFormat(dateString: string): string {
    const parsedDate = dayjs(dateString, "DD-MM-YYYY HH:mm:ss");
    return parsedDate.format("DD-MMM-YYYY");
  }

  function convertDateFormatIndexCard(dateString: string): string {
    const parsedDate = dayjs(dateString, "DD-MM-YYYY");
    return parsedDate.format("DD-MMM-YYYY");
  }

  useEffect(() => {
    if (props?.documentTypes) {
      const key = Object.keys(props?.documentTypes).find((documentKey) =>
        documentKey?.toLowerCase()?.includes("patient"),
      );
      if (key) {
        setKey(key);
        const docTypeList = props?.documentTypes[key]?.map((item) =>
          item?.label?.toLocaleLowerCase(),
        );
        setPatientDocType(docTypeList);
        const findDocumentLabel = props?.documentTypes[key]?.find(
          (doc) => doc?.value === props?.documentType,
        );
        if (findDocumentLabel) {
          setDocumentTypeLabelName(findDocumentLabel?.label);
        }
      }
    }
  }, [props?.documentTypes, props?.documentType]);

  useEffect(() => {
    if (props?.isUploadCardAndReferral == "NO") {
      const uhidValue = form.getFieldValue("uhidNo");
      form.resetFields();
      setServices([]);

      if (
        props?.schemeSelected?.toLowerCase()?.includes("ayushman bharat") &&
        props?.selectedCaseType
      ) {
        form.setFieldValue("caseTypes", props?.selectedCaseType);
      }
      form.setFieldsValue({ uhidNo: uhidValue });
    }
  }, [
    props?.isUploadCardAndReferral,
    props?.schemeSelected,
    props?.selectedCaseType,
  ]);

  useEffect(() => {
    if (
      props?.actualCardList?.some((doc: any) =>
        doc?.documentLabelType?.toLowerCase()?.includes("card"),
      )
    ) {
      setIsCardUploaded(true);
    } else {
      setIsCardUploaded(false);
    }
  }, [props?.actualCardList]);

  useEffect(() => {
    if (
      props?.ocrDetails?.documentLabelType?.toLowerCase()?.includes("referral")
    ) {
      setReferralData(props?.ocrDetails);
      props?.setIsReferralUploaded(true);
    }
  }, [props?.ocrDetails]);

  useEffect(() => {
    if (
      props?.schemeSelected?.toLowerCase()?.includes("ayushman bharat") &&
      props?.selectedCaseType
    ) {
      form.setFieldValue("caseTypes", props?.selectedCaseType);
    }
  }, [props?.schemeSelected, props?.selectedCaseType]);

  useEffect(() => {
    if (props?.isReferralUploaded && props?.ocrDetails?.services?.length > 0) {
      const isReferralTypeProcedure = props?.ocrDetails?.services?.some(
        (service: any) =>
          service?.referralType?.toLowerCase()?.includes("procedure"),
      );
      if (isReferralTypeProcedure) {
        const res = props?.totalCases?.find(
          (caseType) => caseType?.name?.toUpperCase() === "PLANNED IP",
        );

        form.setFieldValue("caseTypes", `${res?.id}~${res?.name}`);
        if (res?.name?.toUpperCase() == "PLANNED IP") {
          setLabelNameForVisit("IP Number");
        }
      } else {
        const res = props?.totalCases?.find((caseType) =>
          caseType?.name?.toUpperCase()?.includes("OP"),
        );
        form.setFieldValue("caseTypes", `${res?.id}~${res?.name}`);
        if (res?.name?.toUpperCase()?.includes("OP")) {
          setLabelNameForVisit("OP Number");
        }
      }
    }
  }, [props?.ocrDetails, props?.isReferralUploaded]);

  useEffect(() => {
    if (props?.totalCases?.length > 0) {
      if (referralData?.referralType?.toUpperCase()?.includes("OPD")) {
        const res = props?.totalCases?.find((caseType) =>
          caseType?.name?.toUpperCase()?.includes("OP"),
        );
        form.setFieldValue("caseTypes", `${res?.id}~${res?.name}`);
        if (res?.name?.toUpperCase()?.includes("OP")) {
          setLabelNameForVisit("OP Number");
        }
      } else if (referralData?.referralType?.toUpperCase()?.includes("IPD")) {
        const res = props?.totalCases?.find(
          (caseType) => caseType?.name?.toUpperCase() === "PLANNED IP",
        );

        form.setFieldValue("caseTypes", `${res?.id}~${res?.name}`);
        if (res?.name?.toUpperCase() == "PLANNED IP") {
          setLabelNameForVisit("IP Number");
        }
      }
    }
  }, [props?.totalCases, referralData]);

  useEffect(() => {
    //to store cardCategory of uploaded card if card is uploaded
    if (props?.ocrDetails?.documentLabelType?.toLowerCase()?.includes("card")) {
      setCardCategory(props?.ocrDetails?.cardCategory);
      form.setFieldValue("cardCategory", props?.ocrDetails?.cardCategory);
      form.setFieldValue("fullName", props?.ocrDetails?.name);
      form.setFieldValue(
        "dob",
        props?.ocrDetails?.dob &&
          props?.ocrDetails?.dob !== "" &&
          dayjs(props?.ocrDetails?.dob, dateFormat, true).isValid()
          ? dayjs(convertDateFormat(props?.ocrDetails?.dob))
          : null,
      );

      if (!props?.schemeSelected?.toLowerCase()?.includes("ayushman bharat")) {
        const numericOnly = props?.ocrDetails?.cardNo?.replace(/\D/g, "");

        setCardBeneficiaryId(numericOnly);
      } else {
        setCardBeneficiaryId(props?.ocrDetails?.cardNo);
      }
    }
  }, [props?.ocrDetails]);

  const identityNumber = useWatch("identityNumber", form);

  useEffect(() => {
    if (identityNumber !== undefined) {
      setCardBeneficiaryId(identityNumber);
    }
  }, [identityNumber]);

  const [cardReferralError, setCardReferralError] = useState<any>(null);

  useEffect(() => {
    if (props?.isReferralUploaded && isCardUploaded) {
      if (
        props?.schemeSelected?.toLowerCase()?.includes("cghs") &&
        referralData?.beneficiaryId &&
        cardBeneficiaryId
      ) {
        if (cardBeneficiaryId !== referralData?.beneficiaryId) {
          setCardReferralError(
            "Details of the card and referral does not match",
          );
          // errorMessage(
          //   "Details of the card and referral does not match, Please check",
          //   8
          // );
        } else {
          setCardReferralError(null);
        }
      } else if (
        props?.schemeSelected?.toLowerCase()?.includes("echs") &&
        referralData?.cardNumber &&
        cardBeneficiaryId
      ) {
        if (cardBeneficiaryId !== referralData?.cardNumber) {
          setCardReferralError(
            "Details of the card and referral does not match",
          );
          // errorMessage(
          //   "Details of the card and referral does not match, Please check",
          //   8
          // );
        } else {
          setCardReferralError(null);
        }
      } else {
        setCardReferralError(null);
      }
    } else {
      setCardReferralError(null);
    }
  }, [
    cardBeneficiaryId,
    isCardUploaded,
    props?.isReferralUploaded,
    props?.schemeSelected,
    referralData,
  ]);

  useEffect(() => {
    if (props?.referralData?.documentType) {
      setReferralData(props?.referralData);

      props?.setIsReferralUploaded(true);
    }
  }, [props?.referralData]);

  useEffect(() => {
    form.resetFields();
    props?.setActualCardList([]);
    props?.setActualIndexCardList([]);
    setServices([]);

    if (
      props?.schemeSelected?.toLowerCase()?.includes("ayushman bharat") &&
      props?.selectedCaseType
    ) {
      form.setFieldValue("caseTypes", props?.selectedCaseType);
    }
  }, [props?.schemeSelected]);

  const onFinish: FormProps<FieldType>["onFinish"] = async (values: any) => {
    const { doctorDetails, ...restValues } = values;
    const allValues = await form.getFieldsValue(true);
    let formattedValues = {
      ...restValues,
      doa: values.doa ? dayjs(values.doa).format(dateFormat) : undefined,
      dod: values.dod ? dayjs(values.dod).format(dateFormat) : undefined,
      registrationDate: values?.registrationDate
        ? dayjs(values?.registrationDate).format(dateFormat)
        : undefined,
    };

    if (doctorDetails) {
      const [doctorId, doctorName] = doctorDetails.split(",");
      formattedValues = {
        ...formattedValues,
        doctorId: parseInt(doctorId.trim()) == 0 ? null : parseInt(doctorId),
        doctorName: doctorName.trim(),
      };
    }

    const updatedValues: FieldType = {
      ...formattedValues,
      uhidNo: "-",
      fullName: values?.fullName,
      age: values?.age,
      sex: values?.sex,
      communicationConsent: allValues?.communicationConsent,
      isPrimaryPolicyHolder: allValues?.isPrimaryPolicyHolder,
      primaryMobileOnWhatsApp: props?.primaryMobileOnWhatsApp,
      secondaryMobileOnWhatsApp: props?.secondaryMobileOnWhatsApp,
      email: allValues?.email,
      dob:
        values.dob && values.dob !== ""
          ? dayjs(values?.dob).format("DD-MM-YYYY HH:mm:ss")
          : allValues.dob && allValues.dob !== ""
            ? dayjs(allValues?.dob).format("DD-MM-YYYY HH:mm:ss")
            : null,
      primaryMobilePrefix: "+91",
      secondaryMobilePrefix: "+91",
      status: "ACTIVE",
      cardCategory: values?.cardCategory || cardCategory,
    };

    // props?.handleSubmitPatient(updatedValues);
    props?.handleShowConfirmPopUp(updatedValues);
  };

  const [patientReferralForm, setPatientReferralForm] =
    useState<FormInstance | null>(null);

  const handleSubmit = async () => {
    try {
      // Validate AddNewPatientForm
      await form.validateFields();

      if (
        props?.schemeSelected?.toLowerCase()?.includes("cghs") &&
        (form.getFieldValue("age") ?? 0) < 70 &&
        !props?.isReferralUploaded &&
        !form.getFieldValue("caseTypes")?.toLowerCase()?.includes("emergency")
      ) {
        message.error(
          "Referral letter is required for CGHS patients below 70 years of age.",
        );
        return;
      }

      if (
        props?.schemeSelected?.toLowerCase()?.includes("echs") &&
        !isCardUploaded
      ) {
        message.error("ECHS card is required for ECHS patients.");
        return;
      }

      if (
        props?.schemeSelected?.toLowerCase()?.includes("echs") &&
        (form.getFieldValue("age") ?? 0) < 70 &&
        !props?.isReferralUploaded &&
        !form.getFieldValue("caseTypes")?.toLowerCase()?.includes("emergency")
      ) {
        message.error(
          "Referral letter is required for ECHS patients below 70 years of age.",
        );
        return;
      }

      await form.getFieldsValue();
      const values = await form.getFieldsValue(true);
      const patientReferralValues = patientReferralForm
        ? await patientReferralForm.getFieldsValue(true)
        : {};

      let data: any;

      if (patientReferralValues?.services) {
        data = services?.map((item: ComponentDetail, index: number) => ({
          id: item?.id,
          name: item.name,
          validUpto:
            item?.validUpto && item?.validUpto !== ""
              ? dayjs(item?.validUpto, "DD-MM-YYYY").format("DD-MM-YYYY")
              : null,
          remarks:
            patientReferralValues[`services[${index}].remarks`] ||
            item.remarks ||
            "",
          maxQty: item.maxQty || "NA",
          referralType: item.referralType || "",
        }));
      } else if (services) {
        data = services?.map((item: ComponentDetail, index: number) => ({
          id: item?.id,
          name: item.name,
          validUpto:
            item?.validUpto && item?.validUpto !== ""
              ? dayjs(item?.validUpto, "DD-MM-YYYY").format("DD-MM-YYYY")
              : null,
          remarks:
            patientReferralValues[`services[${index}].remarks`] ||
            item.remarks ||
            "",
          maxQty: item.maxQty || "NA",
          referralType: item.referralType || "",
        }));
      } else if (servicesManually) {
        data = servicesManually?.map(
          (item: ComponentDetail, index: number) => ({
            id: item?.id,
            name: item.name,
            validUpto:
              item?.validUpto && item?.validUpto !== ""
                ? dayjs(item?.validUpto, "DD-MM-YYYY").format("DD-MM-YYYY")
                : null,
            remarks:
              patientReferralValues[`services[${index}].remarks`] ||
              item.remarks ||
              "",
            maxQty: item.maxQty || "NA",
            referralType: item.referralType || "",
          }),
        );
      }

      const stringTimeIssued =
        patientReferralValues?.timeIssued &&
        patientReferralValues?.timeIssued !== ""
          ? dayjs(patientReferralValues?.timeIssued).format("hh:mm:ss")
          : null;

      const referralInfoRequest = {
        echsReferralRequest: null,
        cghsReferralRequest: null,
      };
      props?.listOfResponseUrl?.forEach((doc) => {
        // if (doc?.documentType?.toLowerCase()?.includes("cghs")) {
        let referralDocument = null;
        if (doc?.documentLabelType?.toLowerCase()?.includes("referral")) {
          let fileType: any = doc.documentName?.includes(".")
            ? doc.documentName.split(".").pop()
            : undefined;
          referralDocument = {
            documentType: doc?.documentType,
            documentUrl: doc?.documentUrl,
            documentName: `${values?.fullName}_${
              patientReferralValues?.refferenceID ||
              patientReferralValues?.referralNo
            }_${dayjs(patientReferralValues?.services?.[0]?.validUpto).format(
              dateFormat,
            )}${fileType ? "." + fileType : ""}`,
            id: doc?.id,
          };
        }
        if (doc?.documentLabelType?.toLowerCase()?.includes("referral")) {
          if (
            props?.schemeSelected?.toLowerCase()?.includes("cghs") ||
            values?.schemes?.toLowerCase()?.includes("cghs") ||
            props?.schemeSelected?.toLowerCase()?.includes("capf") ||
            values?.schemes?.toLowerCase()?.includes("capf")
          ) {
            referralInfoRequest.cghsReferralRequest = {
              ...patientReferralValues,
              id: referralData?.id,
              document: referralDocument,
              services: data,
              schemeId: parseInt((props?.schemeSelected ?? "").split("-")[0]),
              dateIssued:
                patientReferralValues?.dateIssued &&
                patientReferralValues?.dateIssued !== ""
                  ? dayjs(patientReferralValues?.dateIssued).format(
                      "DD-MM-YYYY",
                    )
                  : null,
              timeIssued: stringTimeIssued,
              beneficiaryId: values?.identityNumber,
              name: values?.name || values?.fullName,
              age: values?.age,
              gender: values?.sex || props?.ocrDetails?.gender,
            };
          } else {
            if (!selectedCaseType?.toLowerCase()?.includes("emergency")) {
              referralInfoRequest.echsReferralRequest = {
                ...patientReferralValues,
                id: referralData?.id,
                document: referralDocument,
                schemeId: parseInt((props?.schemeSelected ?? "").split("-")[0]),
                referralNo:
                  referralData?.referralNo ||
                  patientReferralValues?.referralNo ||
                  patientReferralValues?.refferenceID,
                name: values?.name || values?.fullName,
                age: values?.age,
                gender: values?.sex || props?.ocrDetails?.gender,
                referralExpiryFrom:
                  patientReferralValues?.referralExpiryFrom &&
                  patientReferralValues?.referralExpiryFrom !== ""
                    ? dayjs(patientReferralValues?.referralExpiryFrom).format(
                        "DD-MM-YYYY",
                      )
                    : null,
                referralExpiryTo:
                  patientReferralValues?.referralExpiryTo &&
                  patientReferralValues?.referralExpiryTo !== ""
                    ? dayjs(patientReferralValues?.referralExpiryTo).format(
                        "DD-MM-YYYY",
                      )
                    : null,
              };
            } else {
              referralInfoRequest.echsReferralRequest = null;
            }
          }
        }
      });

      let patientSchemeDetails;
      if (scheme?.selectedScheme) {
        patientSchemeDetails = {
          schemeMetaDataId: parseInt(scheme?.selectedScheme.split("-")[0]),
          schemeName: scheme?.selectedScheme.split("-")[1],
          identityNumber: values?.identityNumber,
          patientName: values?.fullName,
          portalMetaDataId: parseInt(scheme.selectedScheme.split("-")[3]),
          status: "ACTIVE",
          gsProviderId: parseInt(scheme?.selectedScheme.split("-")[0]),
          documentId: null,
          policyType: patients?.patientDetails?.policyType,
          policyEndDate: patients?.patientDetails?.policyEndDate
            ? dayjs(
                patients?.patientDetails?.policyEndDate,
                "DD-MM-YYYY HH:mm:ss",
              ).format("DD-MM-YYYY HH:mm:ss")
            : null,
          policySource: patients?.patientDetails?.policySource,
          employeeId: patients?.patientDetails?.employeeId,
          policyStartDate: patients?.patientDetails?.policyStartDate
            ? dayjs(
                patients?.patientDetails?.policyStartDate,
                "DD-MM-YYYY HH:mm:ss",
              ).format("DD-MM-YYYY HH:mm:ss")
            : null,
          derivedGroupPolicy:
            patients?.patientDetails?.derivedGroupPolicy || false,
          policyNumber: patients?.patientDetails?.policyNumber || "",
        };
      }

      const formData = {
        patientRequest: {
          ...values,
          hospitalId: hospital?.selectedHospital?.value,
          patientDocument: props?.listOfResponseUrl?.find((doc) => {
            if (
              doc?.documentLabelType?.toUpperCase() === "CGHS CARD" ||
              doc?.documentLabelType?.toUpperCase() === "CAPF CARD" ||
              doc?.documentLabelType?.toUpperCase()?.includes("CARD")
            ) {
              return {
                documentType: doc?.documentType || "",
                documentName:
                  `${values?.fullName}_${values?.identityNumber}` ||
                  doc?.documentName,
                documentUrl: doc?.documentUrl || "",
                id: doc?.id || null,
              };
            }
          }),
          patientSchemeDetails,
          referralInfoRequest, // Include referral info in patient request
        },
        caseRequestNew: {
          caseTypeMetaDataId: values?.caseTypes?.split("~")[0],
        },
        visitInfoRequest: {
          ...values,
          visitType: values?.caseTypes
            ?.split("~")[1]
            ?.toUpperCase()
            ?.includes("EMERGENCY")
            ? "EMERGENCY"
            : values?.caseTypes?.split("~")[1]?.toUpperCase()?.includes("OP") ||
                values?.caseTypes
                  ?.split("~")[1]
                  ?.toUpperCase()
                  ?.includes("DIALYSIS")
              ? "OP"
              : "IP",
          status: true,
        },
      };

      // Store form data and open confirmation modal
      setPatientFormData(formData);

      onFinish({ ...values, referralInfoRequest });
    } catch (errorInfo) {
      message.error("Please fill all required fields before submitting.");
    }
  };

  const [primaryMobile, setPrimaryMobile] = useState<any>(null);

  useEffect(() => {
    if (!props?.listOfResponseUrl || !props?.isReferralUploaded) {
      setServices([]);
    }
  }, [form, props?.isReferralUploaded]);

  useEffect(() => {
    if (props?.resetForm) {
      form.setFieldValue("identityNumber", "");
      form.setFieldValue("patientName", "");
      form.setFieldValue("cardCategory", null);
    }
  }, [props?.resetForm]);

  useEffect(() => {
    if (props?.patientDetails?.fullName || props?.patientDetails?.name) {
      const sex =
        props?.patientDetails?.sex || props?.patientDetails?.gender || null;
      const normalizedSex =
        sex === "M" || sex === "male"
          ? "Male"
          : sex === "F" || sex === "female"
            ? "Female"
            : sex;

      // const { uhidNo, ...filteredPatientDetails } = props?.patientDetails || {};
      const updatedValues = {
        ...props?.patientDetails,
        fullName:
          props?.patientDetails?.fullName || props?.patientDetails?.name || "",
        dob:
          props?.patientDetails?.dob &&
          props?.patientDetails?.dob !== "" &&
          dayjs(props?.patientDetails?.dob, dateFormat, true).isValid()
            ? dayjs(convertDateFormat(props?.patientDetails?.dob))
            : form.getFieldValue("dob")
              ? form.getFieldValue("dob")
              : null,
        age:
          props?.schemeSelected?.toLowerCase()?.includes("ayushman bharat") &&
          props?.patientDetails?.age
            ? parseInt(
                props?.patientDetails?.age
                  ?.toString()
                  ?.replace(/[^\d.]/g, "")
                  .replace(/\.\d+$/, ""),
              )
            : props?.patientDetails?.age || null,
        primaryMobile: props?.patientDetails?.primaryMobile || null,
        sex: normalizedSex || null,
        communicationConsent: true,
        isPrimaryPolicyHolder: true,
        identityNumber:
          props?.patientDetails?.patientSchemeNewResponseList?.[0]
            ?.schemePolicyNumber ||
          props?.patientDetails?.identityNumber ||
          "",
      };
      form.setFieldsValue(updatedValues);

      setPrimaryMobile(props?.patientDetails?.primaryMobile || null);
      if (
        (props?.patientDetails?.patientSchemeNewResponseList
          ?.schemePolicyNumber ||
          props?.patientDetails?.referralInfoResponse?.cghsReferralResponse
            ?.beneficiaryId ||
          props?.patientDetails?.referralInfoResponse?.echsReferralResponse
            ?.cardNumber ||
          props?.patientDetails?.casesResponseNew?.memberId) &&
        !form.getFieldValue("identityNumber")
      ) {
        form.setFieldValue(
          "identityNumber",
          props?.patientDetails?.patientSchemeNewResponseList
            ?.schemePolicyNumber ||
            props?.patientDetails?.referralInfoResponse?.cghsReferralResponse
              ?.beneficiaryId ||
            props?.patientDetails?.referralInfoResponse?.echsReferralResponse
              ?.cardNumber ||
            props?.patientDetails?.casesResponseNew?.memberId,
        );
        setIsFetchButtonDisabled(false);
      }
      setTimeout(() => form.validateFields(), 0);
    } else if (props?.uhidNo && !props?.patientDetails) {
      form.setFieldValue("uhidNo", props?.uhidNo);
    } else if (
      props?.schemeSelected?.toLowerCase()?.includes("ayushman bharat") &&
      props?.selectedCaseType
    ) {
      form.setFieldValue("caseTypes", props?.selectedCaseType);
    } else if (props?.uhidNo && props?.patientDetails) {
      form.setFieldValue("uhidNo", props?.uhidNo);
    } else {
      form.resetFields();
      setServices([]);
    }
  }, [props?.patientDetails, form, props?.uhidNo, props?.schemeSelected]);

  useEffect(() => {
    if (props?.indexCardSavedPatient?.name) {
      form.setFieldValue("identityNumber", props?.indexCardSavedPatient?.benId);
      form.setFieldValue("patientName", props?.indexCardSavedPatient?.name);
      form.setFieldValue("fullName", props?.indexCardSavedPatient?.name);
      form.setFieldValue("cardCategory", props?.indexCardCategory);
      form.setFieldValue(
        "dob",
        props?.indexCardSavedPatient?.dob &&
          props?.indexCardSavedPatient?.dob !== "" &&
          dayjs(props?.indexCardSavedPatient?.dob, dateFormat, true).isValid()
          ? dayjs(convertDateFormatIndexCard(props?.indexCardSavedPatient?.dob))
          : null,
      );
      form.setFieldValue(
        "age",
        props?.calculateAge(props?.indexCardSavedPatient?.dob),
      );
      setIsFetchButtonDisabled(false);
    }
  }, [props?.indexCardSavedPatient, form, props?.indexCardCategory]);

  useEffect(() => {
    if (props?.registrationSlipSavedPatient?.name) {
      form.setFieldValue(
        "identityNumber",
        props?.registrationSlipSavedPatient?.benId,
      );
      form.setFieldValue("fullName", props?.registrationSlipSavedPatient?.name);
      form.setFieldValue(
        "dob",
        props?.registrationSlipSavedPatient?.dob &&
          props?.registrationSlipSavedPatient?.dob !== "" &&
          dayjs(
            props?.registrationSlipSavedPatient?.dob,
            dateFormat,
            true,
          ).isValid()
          ? dayjs(convertDateFormat(props?.registrationSlipSavedPatient?.dob))
          : null,
      );
      // form.setFieldValue("fullName", props?.registrationSlipSavedPatient?.name);
      form.setFieldValue("cardCategory", props?.registrationSlipCategory);
      setIsFetchButtonDisabled(false);
    }
  }, [
    props?.registrationSlipSavedPatient,
    form,
    props?.registrationSlipCategory,
  ]);

  useEffect(() => {
    if (props?.ocrDetails?.cardNo || props?.ocrDetails?.patientName) {
      let numericOnly = props?.ocrDetails?.cardNo?.replace(/\D/g, "");
      if (props?.schemeSelected?.toLowerCase()?.includes("ayushman bharat")) {
        numericOnly = props?.ocrDetails?.cardNo;
      } else if (props?.schemeSelected?.toLowerCase()?.includes("echs")) {
        numericOnly = numericOnly?.slice(-12);
      }
      if (!form.getFieldValue("identityNumber")) {
        form.setFieldValue("identityNumber", numericOnly);
      }
      form.setFieldValue("identityNumber", numericOnly);
      if (form.getFieldValue("identityNumber") !== null) {
        setIsFetchButtonDisabled(false);
      }

      form.setFieldValue(
        "patientName",
        props?.ocrDetails?.patientName || props?.ocrDetails?.name,
      );
      setTimeout(() => form.validateFields(), 0);
    } else if (
      props?.ocrDetails?.cardNumber ||
      props?.ocrDetails?.name ||
      props?.ocrDetails?.beneficiaryId
    ) {
      if (!form.getFieldValue("identityNumber")) {
        form.setFieldValue(
          "identityNumber",
          props?.ocrDetails?.cardNumber || props?.ocrDetails?.beneficiaryId,
        );
      }
      if (form.getFieldValue("identityNumber") !== null) {
        setIsFetchButtonDisabled(false);
      }
      // if (!form.getFieldValue("fullName")) {
      form.setFieldValue("fullName", props?.ocrDetails?.name);
      // }

      // Set age only if not already set
      if (!form.getFieldValue("age")) {
        form.setFieldValue("age", props?.ocrDetails?.age);
      }

      const sex = props?.ocrDetails?.sex || props?.ocrDetails?.gender || null;
      const normalizedSex =
        sex === "M" || sex === "male"
          ? "Male"
          : sex === "F" || sex === "female"
            ? "Female"
            : sex;
      if (!form.getFieldValue("sex")) {
        form.setFieldValue("sex", normalizedSex);
      }
      const updatedValues = {
        ...props?.ocrDetails,
        dob:
          props?.ocrDetails?.dob &&
          props?.ocrDetails?.dob !== "" &&
          dayjs(props?.ocrDetails?.dob, dateFormat, true).isValid()
            ? dayjs(convertDateFormat(props?.ocrDetails?.dob))
            : form.getFieldValue("dob")
              ? form.getFieldValue("dob")
              : null,

        communicationConsent: true,
        isPrimaryPolicyHolder: true,
      };
      form.setFieldsValue(updatedValues);
      // setTimeout(() => form.validateFields(), 0)
      form.validateFields({ validateOnly: true });
    } else if (props?.ocrDetails?.name || props?.ocrDetails?.age) {
      if (!form.getFieldValue("identityNumber")) {
        form.setFieldValue("identityNumber", props?.ocrDetails?.beneficiaryId);

        if (form.getFieldValue("identityNumber") !== null) {
          setIsFetchButtonDisabled(false);
        }
      }
      // Set name only if not already set
      // if (!form.getFieldValue("fullName")) {
      form.setFieldValue("fullName", props?.ocrDetails?.name);
      // }

      // Set age only if not already set
      if (!form.getFieldValue("age")) {
        form.setFieldValue("age", props?.ocrDetails?.age);
      }

      const sex = props?.ocrDetails?.sex || props?.ocrDetails?.gender || null;
      const normalizedSex =
        sex === "M" || sex === "male"
          ? "Male"
          : sex === "F" || sex === "female"
            ? "Female"
            : sex;
      if (!form.getFieldValue("sex")) {
        form.setFieldValue("sex", normalizedSex);
      }
      const updatedValues = {
        ...props?.ocrDetails,
        dob:
          props?.ocrDetails?.dob &&
          props?.ocrDetails?.dob !== "" &&
          dayjs(props?.ocrDetails?.dob, dateFormat, true).isValid()
            ? dayjs(convertDateFormat(props?.ocrDetails?.dob))
            : form.getFieldValue("dob")
              ? form.getFieldValue("dob")
              : null,

        communicationConsent: true,
        isPrimaryPolicyHolder: true,
      };
      form.setFieldsValue(updatedValues);
      setTimeout(() => form.validateFields(), 0);
    } else if (props?.ocrDetails?.capfData) {
      if (!form.getFieldValue("identityNumber")) {
        const numericOnly = props?.ocrDetails?.cardNo?.replace(/\D/g, "");

        form.setFieldValue("identityNumber", props?.ocrDetails?.capfData);
      }
      if (form.getFieldValue("identityNumber") !== null) {
        setIsFetchButtonDisabled(false);
      }
    }
  }, [form, props?.ocrDetails]);

  const flagForPatientReg = true;

  useEffect(() => {
    const dob = form.getFieldValue("dob");
    if (dob) {
      const age = props?.calculateAge(dob);
      setAge(age || null);
    }
  }, [props?.calculateAge, form, props]);

  useEffect(() => {
    form.setFieldsValue({ age });
  }, [age, form]);

  const caseTypes = Form.useWatch("caseTypes", form);

  useEffect(() => {
    if (
      form.getFieldValue("caseTypes")?.toUpperCase()?.includes("OP") &&
      form.getFieldValue("uhidNo")
    ) {
      form.setFieldsValue({ opNumber: form.getFieldValue("uhidNo") });
    } else {
      form.setFieldsValue({ opNumber: form.getFieldValue("") });
    }
  }, [form.getFieldValue("caseTypes"), caseTypes]);

  useEffect(() => {
    if (!form.getFieldValue("doa")) {
      const now = dayjs();
      form.setFieldValue("doa", dayjs(now, dateFormat));
    }

    if (!form.getFieldValue("registrationDate")) {
      const now = dayjs();
      form.setFieldValue("registrationDate", dayjs(now, dateFormat));
    }
  }, [form.getFieldValue("registrationDate"), form.getFieldValue("doa")]);

  useEffect(() => {
    if (!form.getFieldValue("caseTypes")) {
      const inputAge = form.getFieldValue("age");

      if (
        props?.schemeSelected?.toLowerCase()?.includes("cghs") ||
        props?.schemeSelected?.toLowerCase()?.includes("capf")
      ) {
        if (inputAge && inputAge > 70 && !props?.isReferralUploaded) {
          const res = props?.totalCases?.find((caseType) =>
            caseType?.name?.toUpperCase()?.includes("OP"),
          );
          form.setFieldValue("caseTypes", `${res?.id}~${res?.name}`);
          if (res?.name == "Emergency IP") {
            setLabelNameForVisit("IP Number");
          }
        } else {
          form.setFieldValue("caseTypes", null);
        }
      } else {
        form.setFieldValue("caseTypes", null);
      }
    }
  }, [
    form.getFieldValue("age"),
    props?.isReferralUploaded,
    props?.totalCases,
    props?.patientDetails,
    props?.schemeSelected,
  ]);

  useEffect(() => {
    if (
      !form.getFieldValue("caseTypes") &&
      form.getFieldValue("caseTypes") == ""
    ) {
      if (
        scheme?.selectedScheme?.toUpperCase()?.includes("STAR HEALTH") ||
        scheme?.selectedScheme?.toUpperCase()?.includes("MEDI ASSIST")
      ) {
        const res = props?.totalCases?.find((caseType) =>
          caseType?.name?.toUpperCase()?.includes("PLANNED IP"),
        );
        form.setFieldValue("caseTypes", `${res?.id}~${res?.name}`);
        setLabelNameForVisit("IP Number");
      }
    }
  }, [form.getFieldValue("caseTypes")]);

  const [labelNameForVisit, setLabelNameForVisit] =
    useState<string>("IP/OP NUMBER");
  const [selectedCaseTypeName, setSelectedCaseTypeName] = useState<string>("");

  useEffect(() => {
    if (
      form.getFieldValue("doa") &&
      labelNameForVisit == "IP Number" &&
      !selectedCaseTypeName?.toUpperCase()?.includes("CHEMO") &&
      !selectedCaseTypeName?.toUpperCase()?.includes("CATARACT") &&
      !selectedCaseTypeName?.toUpperCase()?.includes("DAYCARE")
    ) {
      const suggestedDod = form.getFieldValue("doa")?.clone().add(5, "days");
      form.setFieldsValue({ dod: suggestedDod });
    } else if (form.getFieldValue("doa") && labelNameForVisit == "OP Number") {
      const suggestedDod = form.getFieldValue("doa")?.clone();
      form.setFieldsValue({ dod: suggestedDod });
    } else {
      const suggestedDod = form.getFieldValue("doa")?.clone();
      form.setFieldsValue({ dod: suggestedDod });
    }
  }, [labelNameForVisit, selectedCaseTypeName, form.getFieldValue("doa")]);

  const shortenFileName = (
    fileName: string | null | undefined,
    maxLength: number = 10,
  ): string => {
    if (!fileName) {
      return ""; // If fileName is null or undefined, return an empty string
    }

    const fileExtension = fileName.split(".").pop(); // Extract the extension
    if (!fileExtension || fileExtension === fileName) {
      // If there's no extension, or the file name is just an extension (like ".txt")
      return fileName.length > maxLength
        ? fileName.slice(0, maxLength) + "..."
        : fileName;
    }

    const nameWithoutExtension = fileName.slice(0, -fileExtension.length - 1); // Strip the extension
    if (nameWithoutExtension.length > maxLength) {
      return (
        nameWithoutExtension.slice(0, maxLength) + "..." + "." + fileExtension
      );
    }

    return fileName;
  };

  useEffect(() => {
    if (props?.documentTypes && key && props?.listOfUploadDocData) {
      const filteredDocumentType = props?.documentTypes[key]?.filter((item) => {
        const isMatching = props?.listOfUploadDocData?.some(
          (urlItem: any) => urlItem.documentLabelType === item.label,
        );
        return !isMatching;
      });

      props?.setNewDocumentTypes(filteredDocumentType);
    } else {
      props?.setNewDocumentTypes(props?.documentTypes[key]);
    }
  }, [
    props?.listOfUploadDocData,
    props?.documentTypes,
    key,
    props?.isUploadCardAndReferral,
  ]);

  useEffect(() => {
    if (props?.listOfUploadDocData?.length > 0) {
      form.validateFields();
    }
  }, [props?.listOfUploadDocData]);

  useEffect(() => {
    const schemeId = parseInt((props?.schemeSelected ?? "").split("-")[0]);
    if (schemeId == 10 && props?.capfreferal) {
      form.setFieldValue("referral", props?.capfreferal);
    }
  }, [props?.capfreferal]);

  useEffect(() => {
    if (props?.selectedReferral) {
      const isMatch = props?.actualReferralList?.some(
        (referral: any) => referral?.id === props?.selectedReferral?.id,
      );

      if (isMatch) {
        form.validateFields();
      }
    }
  }, [props?.selectedReferral]);

  return (
    <div>
      <Spin spinning={props?.isLoading} size="small">
        <div>
          <div>
            <Form
              name="patient-details-form"
              initialValues={initialValues}
              autoComplete="off"
              form={form}
              layout="vertical"
              style={{ width: "100%" }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }}
            >
              <Row gutter={[8, 8]} justify={"start"}>
                <Col xs={24} sm={24} md={11} lg={11} xl={7}>
                  <Form.Item<FieldType>
                    label="PATIENT TYPE"
                    name="caseTypes"
                    rules={[
                      {
                        required: true,
                        message: "Please select case type!",
                      },
                    ]}
                    style={{ fontWeight: 600 }}
                  >
                    <Select
                      disabled={props?.isDisabled}
                      showSearch
                      size="small"
                      className="visit-type-select"
                      dropdownClassName="visit-type-select-dropdown"
                      placeholder="Select a case type"
                      options={
                        props?.totalCases
                          ? props?.totalCases?.map((caseType) => ({
                              value: caseType?.id + "~" + caseType?.name,
                              label: caseType?.name,
                            }))
                          : []
                      }
                      style={{ width: "100%" }}
                      onChange={(caseType) => {
                        if (caseType && caseType !== "") {
                          setLabelNameForVisit(
                            caseType?.toUpperCase()?.includes("OP")
                              ? "OP Number"
                              : "IP Number",
                          );

                          setSelectedCaseTypeName(caseType);
                        }
                      }}
                      allowClear
                      // disabled={props?.isAddCase}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {selectedCaseTypeName ? (
                <Row
                  // className="form-row"
                  justify={
                    (props?.schemeSelected &&
                      // form.getFieldValue("uhidNo") &&
                      props?.referralList?.length > 0 &&
                      props?.patientDetails) ||
                    props?.isUploadCardAndReferral == "YES" ||
                    props?.isUploadCardAndReferral == ""
                      ? "space-between"
                      : "start"
                  }
                  gutter={[8, 8]}
                >
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                    className="form-col"
                  >
                    {props?.isFetchingDocuments ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Spin />
                      </div>
                    ) : (
                      <Row>
                        {props?.schemeSelected &&
                          // form.getFieldValue("uhidNo") &&
                          // props?.referralList?.length > 0 &&
                          (props?.patientDetails ||
                            props?.isUploadCardAndReferral == "YES") && (
                            <>
                              <Col
                                xs={24}
                                sm={24}
                                md={24}
                                lg={24}
                                xl={24}
                                style={{
                                  marginBottom: "0.4rem",
                                  //   marginTop: "0.4rem",
                                }}
                              >
                                <Form.Item
                                  name="referral"
                                  label={
                                    <span>
                                      CARD{" "}
                                      {props?.actualCardList?.length > 0 &&
                                        (isEditCard ? (
                                          <CloseCircleOutlined
                                            // style={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setIsEditCard(false);
                                            }}
                                          />
                                        ) : (
                                          <EditOutlined
                                            // style={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setIsEditCard(true);
                                            }}
                                          />
                                        ))}
                                    </span>
                                  }
                                  style={{ fontWeight: 600 }}
                                >
                                  {props?.actualCardList &&
                                    props?.actualCardList?.map(
                                      (referral: any) => (
                                        <>
                                          {!form.getFieldValue("fullName") ||
                                          !form.getFieldValue(
                                            "identityNumber",
                                          ) ? (
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "10px",
                                                padding: "6px 10px",
                                                border: "1px solid #f5c2c7",
                                                backgroundColor: "#f8d7da",
                                                borderRadius: "10px",
                                                color: "#842029",
                                                fontSize: "10px",
                                                maxWidth: "600px",
                                                marginBottom: "10px",
                                              }}
                                            >
                                              <ExclamationCircleOutlined
                                                style={{
                                                  fontSize: 10,
                                                  color: "#dc3545",
                                                }}
                                              />
                                              <span
                                                style={{
                                                  fontSize: "8px",
                                                  color: "#D4183D",
                                                }}
                                              >
                                                Sorry, we couldn't fetch number.
                                                Please enter manually.
                                              </span>
                                            </div>
                                          ) : (
                                            <></>
                                          )}

                                          <Card
                                            bordered
                                            style={{
                                              borderRadius: 8,
                                              marginBottom: 12,
                                              fontSize: 12,
                                              border: "1px solid #AADFFF",
                                              // cursor: "pointer",
                                            }}
                                            bodyStyle={{ padding: "6px 8px" }}
                                          >
                                            {/* Top Info */}
                                            <Row gutter={[4, 4]}>
                                              <Col span={12}>
                                                <Text
                                                  type="secondary"
                                                  style={{
                                                    fontSize: 8,
                                                    marginLeft: 3,
                                                    color: "#00395E80",
                                                  }}
                                                >
                                                  {"Name"}
                                                </Text>
                                              </Col>

                                              <Col span={9}>
                                                <Text
                                                  type="secondary"
                                                  style={{
                                                    fontSize: 8,
                                                    color: "#00395E80",
                                                  }}
                                                >
                                                  {"Card Id"}
                                                </Text>
                                              </Col>

                                              <Col
                                                span={3}
                                                style={{
                                                  // textAlign: "right",
                                                  color: "#00060999",
                                                }}
                                              >
                                                <Text
                                                  type="secondary"
                                                  style={{
                                                    fontSize: 8,
                                                    color: "#00395E80",
                                                  }}
                                                >
                                                  Age
                                                </Text>
                                              </Col>
                                            </Row>

                                            <Row gutter={[4, 4]}>
                                              {!form.getFieldValue(
                                                "fullName",
                                              ) || isEditCard ? (
                                                <Col span={12}>
                                                  <Form.Item
                                                    label="PATIENT NAME"
                                                    name="fullName"
                                                    labelCol={{ span: 0 }}
                                                    rules={[
                                                      {
                                                        required: true,
                                                        message:
                                                          "Name is Required!",
                                                      },
                                                    ]}
                                                  >
                                                    <Input
                                                      className="add-patient-input"
                                                      style={{
                                                        borderRadius: 10,
                                                        padding: "4px 8px",
                                                        marginBottom: 2,
                                                        fontSize: 8,
                                                      }}
                                                      autoComplete="off"
                                                      placeholder="ENTER PATIENT NAME"
                                                      disabled={
                                                        props?.isDisabled
                                                      }
                                                    />
                                                  </Form.Item>
                                                </Col>
                                              ) : (
                                                <Col span={12}>
                                                  <Text
                                                    strong
                                                    style={{
                                                      fontSize: 10,
                                                      color: "#00395E",
                                                    }}
                                                  >
                                                    {form.getFieldValue(
                                                      "fullName",
                                                    )}
                                                  </Text>
                                                </Col>
                                              )}

                                              <Col span={9}>
                                                {!form.getFieldValue(
                                                  "identityNumber",
                                                ) || isEditCard ? (
                                                  <Form.Item
                                                    label={"Card Id"}
                                                    name="identityNumber"
                                                    rules={[
                                                      {
                                                        required: true,
                                                        message:
                                                          "Please input identity number!",
                                                      },
                                                    ]}
                                                    labelCol={{ span: 0 }}
                                                  >
                                                    <Input
                                                      className="add-patient-input"
                                                      style={{
                                                        borderRadius: 10,
                                                        padding: "4px 8px",
                                                        marginBottom: 2,
                                                        fontSize: 8,
                                                      }}
                                                      autoComplete="off"
                                                      disabled={
                                                        props?.isDisabled
                                                      }
                                                      placeholder="ENTER YOUR ID"
                                                    />
                                                  </Form.Item>
                                                ) : (
                                                  <Text
                                                    strong
                                                    style={{
                                                      fontSize: 10,
                                                      color: "#00395E",
                                                    }}
                                                  >
                                                    {form.getFieldValue(
                                                      "identityNumber",
                                                    )}
                                                  </Text>
                                                )}
                                              </Col>

                                              <Col span={3}>
                                                {!form.getFieldValue("age") ||
                                                isEditCard ? (
                                                  <Form.Item
                                                    label={"Age"}
                                                    name="age"
                                                    rules={[
                                                      {
                                                        required: true,
                                                        message:
                                                          "Age is Required!",
                                                      },
                                                    ]}
                                                    labelCol={{ span: 0 }}
                                                  >
                                                    <Input
                                                      className="add-patient-input"
                                                      style={{
                                                        borderRadius: 10,
                                                        padding: "4px 8px",
                                                        marginBottom: 2,
                                                        fontSize: 8,
                                                      }}
                                                      autoComplete="off"
                                                      disabled={
                                                        props?.isDisabled
                                                      }
                                                      placeholder="ENTER YOUR Age"
                                                    />
                                                  </Form.Item>
                                                ) : (
                                                  <Text
                                                    strong
                                                    style={{
                                                      fontSize: 10,
                                                      color: "#00395E",
                                                    }}
                                                  >
                                                    {form.getFieldValue("age")}
                                                  </Text>
                                                )}
                                              </Col>
                                            </Row>

                                            <Row gutter={[4, 4]}>
                                              <Col
                                                span={12}
                                                style={{
                                                  // textAlign: "right",
                                                  color: "#00060999",
                                                }}
                                              >
                                                <Text
                                                  type="secondary"
                                                  style={{
                                                    fontSize: 8,
                                                    color: "#00395E80",
                                                  }}
                                                >
                                                  Eligibility
                                                </Text>
                                              </Col>

                                              {/* <Col
                                              span={8}
                                              style={{
                                                // textAlign: "right",
                                                color: "#00060999",
                                              }}
                                            >
                                              <Text
                                                type="secondary"
                                                style={{ fontSize: 8 }}
                                              >
                                                Primary Mobile
                                              </Text>
                                            </Col> */}

                                              <Col
                                                span={10}
                                                style={{
                                                  // textAlign: "right",
                                                  color: "#00060999",
                                                }}
                                              >
                                                <Text
                                                  type="secondary"
                                                  style={{
                                                    fontSize: 8,
                                                    color: "#00395E80",
                                                  }}
                                                >
                                                  Card
                                                </Text>
                                              </Col>
                                            </Row>

                                            <Row gutter={[4, 4]}>
                                              <Col span={12}>
                                                {!form.getFieldValue(
                                                  "cardCategory",
                                                ) || isEditCard ? (
                                                  <Form.Item<FieldType>
                                                    label="CARD CATEGORY"
                                                    name="cardCategory"
                                                    rules={[
                                                      {
                                                        required: true,
                                                        message:
                                                          "Please select card category!",
                                                      },
                                                    ]}
                                                    labelCol={{ span: 0 }}
                                                  >
                                                    <Select
                                                      showSearch
                                                      size="small"
                                                      className="add-patient-select"
                                                      dropdownClassName="add-patient-select-dropdown"
                                                      placeholder="Select a card category"
                                                      options={
                                                        cardCategoryList
                                                          ? cardCategoryList?.map(
                                                              (
                                                                cardCategory,
                                                              ) => ({
                                                                value:
                                                                  cardCategory,
                                                                label:
                                                                  cardCategory,
                                                              }),
                                                            )
                                                          : []
                                                      }
                                                      allowClear
                                                      disabled={
                                                        props?.isDisabled
                                                      }
                                                      // disabled={props?.isAddCase}
                                                    />
                                                  </Form.Item>
                                                ) : (
                                                  <Text
                                                    strong
                                                    style={{
                                                      fontSize: 10,
                                                      color: "#00395E",
                                                    }}
                                                  >
                                                    {form.getFieldValue(
                                                      "cardCategory",
                                                    )}
                                                  </Text>
                                                )}
                                              </Col>

                                              <Col
                                                span={10}
                                                style={{
                                                  marginTop: isEditCard
                                                    ? "5px"
                                                    : "",
                                                }}
                                              >
                                                <EyeOutlined
                                                  style={{
                                                    fontSize: "8px",
                                                  }}
                                                  onClick={() => {
                                                    setPreviewDocument(true);
                                                    setDocumentUrl(
                                                      referral?.documentUrl,
                                                    );
                                                  }}
                                                />
                                                <Tooltip title="DELETE">
                                                  <DeleteOutlined
                                                    style={{
                                                      marginLeft: "10px",
                                                      fontSize: "8px",
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      props?.handleDeleteList(
                                                        referral,
                                                        "cards",
                                                      );
                                                    }}
                                                  />
                                                </Tooltip>
                                              </Col>
                                            </Row>
                                          </Card>
                                        </>
                                      ),
                                    )}

                                  {props?.newDocumentTypes &&
                                    props?.newDocumentTypes?.some(
                                      (item) =>
                                        item?.label
                                          ?.toLowerCase()
                                          ?.includes("card") &&
                                        !item?.label
                                          ?.toLowerCase()
                                          ?.includes("index card"),
                                    ) && (
                                      <Upload
                                        style={{
                                          marginTop: "2px",
                                          width: "100%",
                                        }}
                                        beforeUpload={() => false} // Prevent automatic upload
                                        onChange={props?.handleFileChange}
                                        maxCount={1}
                                        showUploadList={false}
                                      >
                                        <Button
                                          icon={
                                            <CameraOutlined
                                              style={{ color: "#004C8F" }}
                                            />
                                          }
                                          block
                                          style={{
                                            borderStyle: "dashed",
                                            height: 45,
                                            borderRadius: 12,
                                          }}
                                          onClick={() => {
                                            if (props?.newDocumentTypes) {
                                              const referralLetter =
                                                props?.newDocumentTypes.find(
                                                  (item) =>
                                                    item.label
                                                      ?.toLowerCase()
                                                      ?.includes("card"),
                                                );

                                              if (referralLetter) {
                                                props?.handleSelectChange(
                                                  referralLetter.value,
                                                );
                                              }
                                            }
                                          }}
                                        >
                                          <span style={{ fontSize: 11 }}>
                                            Upload Card
                                          </span>
                                        </Button>
                                      </Upload>
                                    )}
                                </Form.Item>
                              </Col>
                              {parseInt(
                                (props?.schemeSelected ?? "").split("-")[0],
                              ) !== 3 &&
                                parseInt(
                                  (props?.schemeSelected ?? "").split("-")[0],
                                ) !== 4 &&
                                patientDocType?.some((item) =>
                                  item.includes("referral"),
                                ) &&
                                !selectedCaseType
                                  ?.toLowerCase()
                                  ?.includes("emergency") && (
                                  <Col
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    lg={24}
                                    xl={24}
                                    style={{
                                      marginBottom: "0.4rem",
                                      //   marginTop: "0.4rem",
                                    }}
                                  >
                                    <Form.Item
                                      name="referral"
                                      label={
                                        <span>
                                          {props?.actualReferralList?.length > 1
                                            ? "REFERRAL LETTERS"
                                            : "REFERRAL LETTER"}{" "}
                                          {/* {props?.actualReferralList?.length >
                                            0 &&
                                            (isEditReferral ? (
                                              <CloseCircleOutlined
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setIsEditReferral(false);
                                                }}
                                              />
                                            ) : (
                                              <EditOutlined
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setIsEditReferral(true);
                                                }}
                                              />
                                            ))} */}
                                        </span>
                                      }
                                      rules={[
                                        {
                                          required:
                                            props?.actualReferralList?.length >
                                              0 && !props?.selectedReferral,
                                          message:
                                            "Please select a referral Letter",
                                        },
                                      ]}
                                      style={{ fontWeight: 600 }}
                                    >
                                      {props?.actualReferralList &&
                                        props?.actualReferralList?.map(
                                          (referral: any) => {
                                            const hoverName =
                                              referral?.documentName;
                                            const formattedName =
                                              hoverName?.split(
                                                " 00:00:00.jpg",
                                              )[0];
                                            return (
                                              //   <div
                                              //     key={referral?.id}
                                              //     style={{
                                              //       position: "relative",
                                              //       display: "inline-block",
                                              //       marginBottom: ".6rem",
                                              //     }}
                                              //   >
                                              //     <Tooltip title={formattedName}>
                                              //       <Button
                                              //         style={{
                                              //           backgroundColor:
                                              //             props?.selectedReferral
                                              //               ?.id === referral?.id
                                              //               ? "#004c8f"
                                              //               : "",
                                              //           color:
                                              //             props?.selectedReferral
                                              //               ?.id === referral?.id
                                              //               ? "white"
                                              //               : "#004c8f",
                                              //           marginRight: "10px",
                                              //           fontSize: "0.8rem",
                                              //           fontWeight: "400",
                                              //         }}
                                              //         onClick={(e) => {
                                              //           props?.setSelectedReferral(
                                              //             referral,
                                              //           );
                                              //           props?.handleReferralChange(
                                              //             referral,
                                              //           );
                                              //         }}
                                              //         icon={
                                              //           <>
                                              //             <Tooltip title="VIEW">
                                              //               <EyeOutlined
                                              //                 style={{
                                              //                   fontSize:
                                              //                     "0.8rem",
                                              //                 }}
                                              //                 onClick={(e) => {
                                              //                   e.stopPropagation();
                                              //                   props?.handleShowConfirmModal2(
                                              //                     referral?.documentUrl,
                                              //                   );
                                              //                 }}
                                              //               />
                                              //             </Tooltip>
                                              //             <Tooltip title="DELETE">
                                              //               <DeleteOutlined
                                              //                 style={{
                                              //                   marginLeft:
                                              //                     "10px",
                                              //                   fontSize:
                                              //                     "0.8rem",
                                              //                 }}
                                              //                 onClick={(e) => {
                                              //                   e.stopPropagation();
                                              //                   props?.handleDeleteList(
                                              //                     referral,
                                              //                     "referral",
                                              //                   );
                                              //                 }}
                                              //               />
                                              //             </Tooltip>
                                              //           </>
                                              //         }
                                              //       >
                                              //         <span
                                              //           style={{
                                              //             fontSize: "0.8rem",
                                              //           }}
                                              //         >
                                              //           {shortenFileName(
                                              //             referral?.documentName,
                                              //           ) ||
                                              //             referral?.documentLabelType}
                                              //         </span>
                                              //       </Button>
                                              //     </Tooltip>
                                              //   </div>
                                              <ReferralCard
                                                schemeSelected={
                                                  props?.schemeSelected
                                                }
                                                referral={referralData}
                                                // referral={
                                                //   formatDates(patientReferralForm?.getFieldsValue(
                                                //     true,
                                                //   )) || referralData
                                                // }
                                                handleDeleteList={
                                                  props?.handleDeleteList
                                                }
                                                setDocumentUrl={setDocumentUrl}
                                                setPreviewDocument={
                                                  setPreviewDocument
                                                }
                                              />
                                            );
                                          },
                                        )}

                                      {props?.newDocumentTypes &&
                                        props?.newDocumentTypes?.some((item) =>
                                          item.label
                                            ?.toLowerCase()
                                            ?.includes("referral"),
                                        ) && (
                                          <Upload
                                            style={{
                                              marginTop: "2px",
                                              width: "100%",
                                            }}
                                            beforeUpload={() => false} // Prevent automatic upload
                                            onChange={props?.handleFileChange}
                                            maxCount={1}
                                            showUploadList={false}
                                          >
                                            <Button
                                              icon={
                                                <CameraOutlined
                                                  style={{ color: "#004C8F" }}
                                                />
                                              }
                                              block
                                              style={{
                                                borderStyle: "dashed",
                                                height: 45,
                                                borderRadius: 12,
                                              }}
                                              onClick={() => {
                                                if (props?.newDocumentTypes) {
                                                  const referralLetter =
                                                    props?.newDocumentTypes.find(
                                                      (item) =>
                                                        item.label
                                                          ?.toLowerCase()
                                                          ?.includes(
                                                            "referral",
                                                          ),
                                                    );

                                                  if (referralLetter) {
                                                    props?.handleSelectChange(
                                                      referralLetter.value,
                                                    );
                                                  }
                                                }
                                              }}
                                            >
                                              <span style={{ fontSize: 11 }}>
                                                Upload Referral
                                              </span>
                                            </Button>
                                          </Upload>
                                        )}
                                    </Form.Item>
                                  </Col>
                                )}

                                {patientDocType?.some((item) =>
                                item?.toLowerCase().includes("index"),
                              ) && (
                                <Col
                                  xs={24}
                                  sm={24}
                                  md={24}
                                  lg={24}
                                  xl={24}
                                  style={{
                                    marginBottom: "0.4rem",
                                    // marginTop: "0.4rem",
                                  }}
                                >
                                  <Form.Item
                                    name="referral"
                                    label={
                                      <span>
                                        INDEX CARD{" "}
                                        {props?.actualIndexCardList?.length >
                                          0 &&
                                          (isEditIndexCard ? (
                                            <CloseCircleOutlined
                                              // style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setIsEditIndexCard(false);
                                              }}
                                            />
                                          ) : (
                                            <EditOutlined
                                              // style={{ cursor: "pointer" }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setIsEditIndexCard(true);
                                              }}
                                            />
                                          ))}
                                      </span>
                                    }
                                    style={{ fontWeight: 600 }}
                                  >
                                    {props?.actualIndexCardList &&
                                      props?.actualIndexCardList?.map(
                                        (referral: any) => (
                                          //   <Tooltip
                                          //     key={referral?.id}
                                          //     title={referral?.documentName}
                                          //   >
                                          //     <Button
                                          //       className="document-button"
                                          //       style={{
                                          //         backgroundColor: "#004c8f",
                                          //         color: "white",
                                          //         marginRight: "0.5vh",
                                          //         marginBottom: "0.5vh",
                                          //         fontSize: "0.8rem",
                                          //         fontWeight: "400",
                                          //       }}
                                          //       icon={
                                          //         <>
                                          //           <Tooltip title="VIEW">
                                          //             <EyeOutlined
                                          //               style={{
                                          //                 fontSize: "0.8rem",
                                          //               }}
                                          //               onClick={(e) => {
                                          //                 e.stopPropagation();
                                          //                 props?.handleShowConfirmModal2(
                                          //                   referral?.documentUrl,
                                          //                 );
                                          //               }}
                                          //             />
                                          //           </Tooltip>
                                          //           <Tooltip title="DELETE">
                                          //             <DeleteOutlined
                                          //               style={{
                                          //                 marginLeft: "0.5vh",
                                          //                 fontSize: "0.8rem",
                                          //               }}
                                          //               onClick={(e) => {
                                          //                 e.stopPropagation();
                                          //                 props?.handleDeleteList(
                                          //                   referral,
                                          //                   "index",
                                          //                 );
                                          //               }}
                                          //             />
                                          //           </Tooltip>
                                          //         </>
                                          //       }
                                          //     >
                                          //       <span
                                          //         style={{ fontSize: "0.8rem" }}
                                          //       >
                                          //         {shortenFileName(
                                          //           referral?.documentName,
                                          //         ) ||
                                          //           referral?.documentLabelType}
                                          //       </span>
                                          //     </Button>
                                          //   </Tooltip>
                                          <>
                                            {!form.getFieldValue("fullName") ||
                                            !form.getFieldValue(
                                              "identityNumber",
                                            ) ? (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "10px",
                                                  padding: "6px 10px",
                                                  border: "1px solid #f5c2c7",
                                                  backgroundColor: "#f8d7da",
                                                  borderRadius: "10px",
                                                  color: "#842029",
                                                  fontSize: "10px",
                                                  maxWidth: "600px",
                                                  marginBottom: "10px",
                                                }}
                                              >
                                                <ExclamationCircleOutlined
                                                  style={{
                                                    fontSize: 10,
                                                    color: "#dc3545",
                                                  }}
                                                />
                                                <span
                                                  style={{
                                                    fontSize: "8px",
                                                    color: "#D4183D",
                                                  }}
                                                >
                                                  Sorry, we couldn't fetch
                                                  number. Please enter manually.
                                                </span>
                                              </div>
                                            ) : (
                                              <></>
                                            )}

                                            <Card
                                              bordered
                                              style={{
                                                borderRadius: 8,
                                                marginBottom: 12,
                                                fontSize: 12,
                                                border: "1px solid #AADFFF",
                                                // cursor: "pointer",
                                              }}
                                              bodyStyle={{ padding: "6px 8px" }}
                                            >
                                              {/* Top Info */}
                                              <Row gutter={[4, 4]}>
                                                <Col span={12}>
                                                  <Text
                                                    type="secondary"
                                                    style={{
                                                      fontSize: 8,
                                                      //   marginLeft: 3,
                                                      color: "#00395E80",
                                                    }}
                                                  >
                                                    {"Name"}
                                                  </Text>
                                                </Col>

                                                <Col span={9}>
                                                  <Text
                                                    type="secondary"
                                                    style={{
                                                      fontSize: 8,
                                                      color: "#00395E80",
                                                    }}
                                                  >
                                                    {"Card Id"}
                                                  </Text>
                                                </Col>

                                                <Col
                                                  span={3}
                                                  style={{
                                                    // textAlign: "right",
                                                    color: "#00395E80",
                                                  }}
                                                >
                                                  <Text
                                                    type="secondary"
                                                    style={{
                                                      fontSize: 8,
                                                      color: "#00395E80",
                                                    }}
                                                  >
                                                    Age
                                                  </Text>
                                                </Col>
                                              </Row>

                                              <Row gutter={[4, 4]}>
                                                {!form.getFieldValue(
                                                  "fullName",
                                                ) || isEditIndexCard ? (
                                                  <Col span={12}>
                                                    <Form.Item
                                                      label="PATIENT NAME"
                                                      name="fullName"
                                                      labelCol={{ span: 0 }}
                                                      rules={[
                                                        {
                                                          required: true,
                                                          message:
                                                            "Name is Required!",
                                                        },
                                                      ]}
                                                    >
                                                      <Input
                                                        className="add-patient-input"
                                                        style={{
                                                          borderRadius: 10,
                                                          padding: "4px 8px",
                                                          marginBottom: 2,
                                                          fontSize: 8,
                                                        }}
                                                        autoComplete="off"
                                                        placeholder="ENTER PATIENT NAME"
                                                        disabled={
                                                          props?.isDisabled
                                                        }
                                                      />
                                                    </Form.Item>
                                                  </Col>
                                                ) : (
                                                  <Col span={12}>
                                                    <Text
                                                      strong
                                                      style={{
                                                        fontSize: 10,
                                                        color: "#00395E",
                                                      }}
                                                    >
                                                      {form.getFieldValue(
                                                        "fullName",
                                                      )}
                                                    </Text>
                                                  </Col>
                                                )}

                                                <Col span={9}>
                                                  {!form.getFieldValue(
                                                    "identityNumber",
                                                  ) || isEditIndexCard ? (
                                                    <Form.Item
                                                      label={"Card Id"}
                                                      name="identityNumber"
                                                      rules={[
                                                        {
                                                          required: true,
                                                          message:
                                                            "Please input identity number!",
                                                        },
                                                      ]}
                                                      labelCol={{ span: 0 }}
                                                    >
                                                      <Input
                                                        className="add-patient-input"
                                                        style={{
                                                          borderRadius: 10,
                                                          padding: "4px 8px",
                                                          marginBottom: 2,
                                                          fontSize: 8,
                                                        }}
                                                        autoComplete="off"
                                                        disabled={
                                                          props?.isDisabled
                                                        }
                                                        placeholder="ENTER YOUR ID"
                                                      />
                                                    </Form.Item>
                                                  ) : (
                                                    <Text
                                                      strong
                                                      style={{
                                                        fontSize: 10,
                                                        color: "#00395E",
                                                      }}
                                                    >
                                                      {form.getFieldValue(
                                                        "identityNumber",
                                                      )}
                                                    </Text>
                                                  )}
                                                </Col>

                                                <Col span={3}>
                                                  {!form.getFieldValue("age") ||
                                                  isEditIndexCard ? (
                                                    <Form.Item
                                                      label={"Age"}
                                                      name="age"
                                                      rules={[
                                                        {
                                                          required: true,
                                                          message:
                                                            "Age is Required!",
                                                        },
                                                      ]}
                                                      labelCol={{ span: 0 }}
                                                    >
                                                      <Input
                                                        className="add-patient-input"
                                                        style={{
                                                          borderRadius: 10,
                                                          padding: "4px 8px",
                                                          marginBottom: 2,
                                                          fontSize: 8,
                                                        }}
                                                        autoComplete="off"
                                                        disabled={
                                                          props?.isDisabled
                                                        }
                                                        placeholder="ENTER YOUR Age"
                                                      />
                                                    </Form.Item>
                                                  ) : (
                                                    <Text
                                                      strong
                                                      style={{
                                                        fontSize: 10,
                                                        color: "#00395E",
                                                      }}
                                                    >
                                                      {form.getFieldValue(
                                                        "age",
                                                      )}
                                                    </Text>
                                                  )}
                                                </Col>
                                              </Row>

                                              <Row gutter={[4, 4]}>
                                                <Col
                                                  span={12}
                                                  style={{
                                                    // textAlign: "right",
                                                    color: "#00395E80",
                                                  }}
                                                >
                                                  <Text
                                                    type="secondary"
                                                    style={{
                                                      fontSize: 8,
                                                      color: "#00395E80",
                                                    }}
                                                  >
                                                    Eligibility
                                                  </Text>
                                                </Col>

                                                <Col
                                                  span={10}
                                                  style={{
                                                    // textAlign: "right",
                                                    color: "#00060999",
                                                  }}
                                                >
                                                  <Text
                                                    type="secondary"
                                                    style={{
                                                      fontSize: 8,
                                                      color: "#00395E80",
                                                    }}
                                                  >
                                                    Card
                                                  </Text>
                                                </Col>
                                              </Row>

                                              <Row gutter={[4, 4]}>
                                                <Col span={12}>
                                                  {!form.getFieldValue(
                                                    "cardCategory",
                                                  ) || isEditIndexCard ? (
                                                    <Form.Item<FieldType>
                                                      label="CARD CATEGORY"
                                                      name="cardCategory"
                                                      rules={[
                                                        {
                                                          required: true,
                                                          message:
                                                            "Please select card category!",
                                                        },
                                                      ]}
                                                      labelCol={{ span: 0 }}
                                                    >
                                                      <Select
                                                        showSearch
                                                        size="small"
                                                        className="add-patient-select"
                                                        dropdownClassName="add-patient-select-dropdown"
                                                        placeholder="Select a card category"
                                                        options={
                                                          cardCategoryList
                                                            ? cardCategoryList?.map(
                                                                (
                                                                  cardCategory,
                                                                ) => ({
                                                                  value:
                                                                    cardCategory,
                                                                  label:
                                                                    cardCategory,
                                                                }),
                                                              )
                                                            : []
                                                        }
                                                        allowClear
                                                        disabled={
                                                          props?.isDisabled
                                                        }
                                                        // disabled={props?.isAddCase}
                                                      />
                                                    </Form.Item>
                                                  ) : (
                                                    <Text
                                                      strong
                                                      style={{
                                                        fontSize: 10,
                                                        color: "#00395E",
                                                      }}
                                                    >
                                                      {form.getFieldValue(
                                                        "cardCategory",
                                                      )}
                                                    </Text>
                                                  )}
                                                </Col>

                                                <Col
                                                  span={10}
                                                  style={{
                                                    marginTop: isEditIndexCard
                                                      ? "5px"
                                                      : "",
                                                  }}
                                                >
                                                  <EyeOutlined
                                                    style={{
                                                      fontSize: "8px",
                                                    }}
                                                    onClick={() => {
                                                      setPreviewDocument(true);
                                                      setDocumentUrl(
                                                        referral?.documentUrl,
                                                      );
                                                    }}
                                                  />
                                                  <Tooltip title="DELETE">
                                                    <DeleteOutlined
                                                      style={{
                                                        marginLeft: "10px",
                                                        fontSize: "8px",
                                                      }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        props?.handleDeleteList(
                                                          referral,
                                                          "index",
                                                        );
                                                      }}
                                                    />
                                                  </Tooltip>
                                                </Col>
                                              </Row>
                                            </Card>
                                          </>
                                        ),
                                      )}

                                    {props?.newDocumentTypes &&
                                      props?.newDocumentTypes?.some((item) =>
                                        item?.label
                                          ?.toLowerCase()
                                          ?.includes("index"),
                                      ) && (
                                        <Upload
                                          style={{
                                            marginTop: "2px",
                                            width: "100%",
                                          }}
                                          beforeUpload={() => false} // Prevent automatic upload
                                          onChange={props?.handleFileChange}
                                          maxCount={1}
                                          showUploadList={false}
                                        >
                                          <Button
                                            icon={
                                              <CameraOutlined
                                                style={{ color: "#004C8F" }}
                                              />
                                            }
                                            block
                                            style={{
                                              borderStyle: "dashed",
                                              height: 45,
                                              borderRadius: 12,
                                            }}
                                            onClick={() => {
                                              if (props?.newDocumentTypes) {
                                                const referralLetter =
                                                  props?.newDocumentTypes?.find(
                                                    (item) =>
                                                      item.label
                                                        ?.toLowerCase()
                                                        ?.includes("index"),
                                                  );

                                                if (referralLetter) {
                                                  props?.handleSelectChange(
                                                    referralLetter.value,
                                                  );
                                                }
                                              }
                                            }}
                                          >
                                            <span style={{ fontSize: 11 }}>
                                              Upload Index Card
                                            </span>
                                          </Button>
                                        </Upload>
                                      )}
                                  </Form.Item>
                                </Col>
                              )}
                            </>
                          )}

                        {props?.schemeSelected &&
                        props?.schemeSelected?.length > 0 ? (
                          // && form.getFieldValue("uhidNo")
                          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <PatientDocumentUpload
                              documentType={props?.documentType}
                              setDocumentType={props?.setDocumentType}
                              handleSaveReferral={props?.handleSaveReferral}
                              listOfResponseUrl={props?.listOfResponseUrl}
                              documentTypes={props?.documentTypes[key]}
                              selectedFile={props?.selectedFile}
                              uploadFileModal={props?.uploadFileModal}
                              setUploadFileModal={props?.setUploadFileModal}
                              handleSelectChange={props?.handleSelectChange}
                              handleFileChange={props?.handleFileChange}
                              viewFile={props?.viewFile}
                              isEditReferral={true}
                              handleDownloadDocument={
                                props?.handleDownloadDocument
                              }
                              isDownloadingDocument={
                                props?.isDownloadingDocument
                              }
                              downloadingDocumentUrl={
                                props?.downloadingDocumentUrl
                              }
                              handleDeleteList={props?.handleDeleteList}
                              isReferralLoading={props?.isReferralLoading}
                              isDisabledDocumentValue={false}
                              type={"referral"}
                              handleShowConfirmModal={
                                props?.handleShowConfirmModal
                              }
                              showCompareModal={props?.showCompareModal}
                              previewDocumentUrl={props?.previewDocumentUrl}
                              documentTypeLabelName={documentTypeLabelName}
                              handleDeficiencyModal={
                                props?.handleDeficiencyModal
                              }
                              closeDeficiencyModal={props?.closeDeficiencyModal}
                              deficiencyModal={props?.deficiencyModal}
                              deficiencyQuestions={props?.deficiencyQuestions}
                              selectedDocument={props?.selectedDocument}
                              handleUploadCapfReferral={
                                props?.handleUploadCapfReferral
                              }
                              schemeSelected={props?.schemeSelected}
                              uhidNo={form.getFieldValue("uhidNo")}
                            />
                          </Col>
                        ) : (
                          <></>
                        )}
                        {/* <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          {props?.schemeSelected &&
                            props?.schemeSelected?.length > 0 && (
                              //&& form.getFieldValue("uhidNo") &&
                              <>
                                <Row gutter={16} align="middle">
                                  <Col
                                    xs={24}
                                    sm={24}
                                    md={
                                      props?.schemeSelected
                                        .split("-")[2]
                                        ?.includes("PATIENT NAME")
                                        ? 8
                                        : 8
                                    }
                                    lg={
                                      props?.schemeSelected
                                        .split("-")[2]
                                        ?.includes("PATIENT NAME")
                                        ? 8
                                        : 8
                                    }
                                    xl={
                                      props?.schemeSelected
                                        .split("-")[2]
                                        ?.includes("PATIENT NAME")
                                        ? 8
                                        : 8
                                    }
                                  >
                                    <Form.Item
                                      label={
                                        `${
                                          props?.schemeSelected.split("-")[2]
                                        }`.includes("+")
                                          ? props?.extractTextBeforePlus(
                                              props?.schemeSelected.split(
                                                "-",
                                              )[2],
                                            )
                                          : props?.schemeSelected.split("-")[2]
                                      }
                                      name="identityNumber"
                                      rules={[
                                        {
                                          required: true,
                                          message:
                                            "Please input identity number!",
                                        },
                                      ]}
                                    >
                                      <Input
                                        autoComplete="off"
                                        disabled={props?.isDisabled}
                                        placeholder="ENTER YOUR ID"
                                      />
                                    </Form.Item>
                                  </Col>

                                  {props?.schemeSelected
                                    .split("-")[2]
                                    ?.includes("PATIENT NAME") && (
                                    <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                                      <Form.Item
                                        label="PATIENT NAME"
                                        name="patientName"
                                      >
                                        <Input
                                          autoComplete="off"
                                          placeholder="ENTER PATIENT NAME"
                                          disabled={props?.isDisabled}
                                        />
                                      </Form.Item>
                                    </Col>
                                  )}

                                  {props?.schemeSelected
                                    ?.toLowerCase()
                                    ?.includes("cghs") ||
                                  props?.schemeSelected
                                    ?.toLowerCase()
                                    ?.includes("echs") ? (
                                    <Col
                                      xs={24}
                                      sm={24}
                                      md={
                                        props?.schemeSelected
                                          .split("-")[2]
                                          ?.includes("PATIENT NAME")
                                          ? 8
                                          : 8
                                      }
                                      lg={
                                        props?.schemeSelected
                                          .split("-")[2]
                                          ?.includes("PATIENT NAME")
                                          ? 8
                                          : 8
                                      }
                                      xl={
                                        props?.schemeSelected
                                          .split("-")[2]
                                          ?.includes("PATIENT NAME")
                                          ? 8
                                          : 8
                                      }
                                    >
                                      <Form.Item<FieldType>
                                        label="CARD CATEGORY"
                                        name="cardCategory"
                                        rules={[
                                          {
                                            required: true,
                                            message:
                                              "Please select card category!",
                                          },
                                        ]}
                                      >
                                        <Select
                                          showSearch
                                          placeholder="Select a card category"
                                          options={
                                            cardCategoryList
                                              ? cardCategoryList?.map(
                                                  (cardCategory) => ({
                                                    value: cardCategory,
                                                    label: cardCategory,
                                                  }),
                                                )
                                              : []
                                          }
                                          allowClear
                                          disabled={props?.isDisabled}
                                          // disabled={props?.isAddCase}
                                        />
                                      </Form.Item>
                                    </Col>
                                  ) : (
                                    <></>
                                  )}
                                </Row>
                              </>
                            )}
                        </Col> */}
                      </Row>
                    )}
                  </Col>
                  {props?.schemeSelected && // form.getFieldValue("uhidNo") &&
                    (props?.patientDetails ||
                      props?.isReferralUploaded ||
                      props?.ocrDetails ||
                      props?.showPatientFields) && (
                      <Col
                        style={{
                          borderRadius: "12px",
                          //   backgroundColor:
                          //     "linear-gradient(135deg, #f0fafb 0%, #ffffff 100%)",
                          //   border: "1px solid #e0e0e0",
                          //   padding: "1.5rem",
                          //   boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                        }}
                        xs={24}
                        sm={24}
                        md={24}
                        lg={props?.showCompareModal ? 24 : 11}
                        xl={props?.showCompareModal ? 24 : 11}
                      >
                        <Row justify="space-between" gutter={[16, 16]}>
                          {/* <Col md={11} sm={24} xs={24}>
                            <Form.Item<FieldType>
                              label="PATIENT NAME"
                              name="fullName"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input full name!",
                                },
                                {
                                  pattern: /^[A-Za-z\s./]+$/,
                                  message:
                                    "Name can only contain letters, spaces, and '/'.",
                                },
                              ]}
                            >
                              <Input
                                maxLength={50}
                                autoComplete="off"
                                disabled={props?.isDisabled}
                                placeholder="ENTER PATIENT NAME"
                              />
                            </Form.Item>
                          </Col> */}

                          {/* <Col
                            xs={24}
                            sm={24}
                            md={11}

                            // style={{ height: "83px" }}
                          >
                            <Form.Item<FieldType>
                              label="PRIMARY MOBILE"
                              validateFirst
                              name="primaryMobile"
                              // validateStatus="error"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input number!",
                                },
                                {
                                  pattern: /^[6-9]/,
                                  message: "Number start with 6, 7, 8, or 9!",
                                },
                                {
                                  pattern: /^\d{10}$/,
                                  message: "Number must be 10 digits!",
                                },
                                {
                                  pattern: /^[6-9]\d{9}$/,
                                  message: "Numeric values are allow!",
                                },
                              ]}
                            >
                              <Flex
                                align="center"
                                gap={"small"}
                                justify="space-between"
                                style={{ width: "100%" }}
                              >
                                <Input
                                  // addonBefore={primaryMobilePrefixSelector}
                                  style={{ width: "100%" }}
                                  autoComplete="off"
                                  placeholder="ENTER YOUR PRIMARY NUMBER"
                                  disabled={props?.isDisabled}
                                  maxLength={10}
                                  onChange={(e) =>
                                    setPrimaryMobile(e.target.value)
                                  }
                                  value={primaryMobile}
                                />
                              </Flex>
                            </Form.Item>
                          </Col> */}
                        </Row>
                      </Col>
                    )}
                </Row>
              ) : (
                <></>
              )}
              {props?.schemeSelected &&
                (props?.patientDetails ||
                  props?.isReferralUploaded ||
                  props?.ocrDetails ||
                  props?.showPatientFields) && (
                  <>
                    {/* <Divider
                      orientation={"left" as any}
                      style={{
                        borderColor: "#bcbcbc",
                        fontWeight: "500",
                        padding: " 0px 10px",
                        color: " rgb(166, 166, 166)",
                        fontSize: "1rem",
                      }}
                      className="divider-section"
                    >
                      VISIT INFORMATION
                    </Divider> */}

                    {/* <Row justify="space-between">
                      {props?.schemeSelected
                        ?.toLowerCase()
                        ?.includes("cghs") ? (
                        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                          <Form.Item
                            label="REGISTRATION DATE"
                            name="registrationDate"
                            rules={[
                              {
                                required: true,
                                message: "Please input Registration Date",
                              },
                            ]}
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              format={dateFormat}
                              disabledDate={(current) => {
                                const now = dayjs().endOf("day");
                                const firstAllowedDate =
                                  dayjs().startOf("year");

                                return (
                                  current.isBefore(firstAllowedDate, "day") ||
                                  current.isAfter(now, "day")
                                );
                              }}
                            />
                          </Form.Item>
                        </Col>
                      ) : (
                        <></>
                      )}

                      {props?.schemeSelected
                        ?.toLowerCase()
                        ?.includes("cghs") ? (
                        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                          <Form.Item
                            label="REGISTRATION ID"
                            name="registrationId"
                            rules={[
                              {
                                required: true,
                                message: "Please input REGISTRATION ID",
                              },
                              {
                                pattern: /^\S+$/,
                                message:
                                  "REGISTRATION ID must not contain spaces",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Please input REGISTRATION ID"
                              //   disabled={portalData?.schemeRegistrationId}
                              style={{ color: "black" }}
                            />
                          </Form.Item>
                        </Col>
                      ) : (
                        <></>
                      )}

                      {props?.schemeSelected?.toLowerCase()?.includes("echs") &&
                      (selectedCaseType?.toLowerCase()?.includes("emergency") ||
                        (selectedCaseType?.toLowerCase()?.includes("op") &&
                          !props?.isReferralUploaded &&
                          (form.getFieldValue("age") ?? 0) >= 70)) ? (
                        <Col xs={24} sm={24} md={5} lg={5} xl={5}>
                          <Form.Item
                            label="AADHAR CARD STATUS"
                            name={"aadharStatus"}
                            rules={[
                              {
                                required: true,
                                message: "Aadhar Card Status is required",
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select Aadhar Card Status"
                              size="small"
                              className="visit-type-select"
                              dropdownClassName="visit-type-select-dropdown"
                              optionRender={(option) => (
                                <div
                                  style={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    maxWidth: "100%",
                                  }}
                                >
                                  {option.label}
                                </div>
                              )}
                              allowClear
                            >
                              {aadharStatusOptions?.map((option) => (
                                <Option key={option.value} value={option.value}>
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      ) : (
                        <></>
                      )}

                      {props?.schemeSelected?.toLowerCase()?.includes("echs") &&
                      (selectedCaseType?.toLowerCase()?.includes("emergency") ||
                        (selectedCaseType?.toLowerCase()?.includes("op") &&
                          !props?.isReferralUploaded &&
                          (form.getFieldValue("age") ?? 0) >= 70)) ? (
                        <Col xs={24} sm={24} md={5} lg={5} xl={5}>
                          <Form.Item
                            label="AADHAR CARD NUMBER"
                            name={"aadharNumber"}
                            rules={[
                              {
                                required:
                                  aadharSelectedValue ===
                                  "Patient has an Aadhaar Number"
                                    ? true
                                    : false,
                                message: "Aadhar Card Number is required",
                              },
                              {
                                pattern: /^[2-9]{1}[0-9]{11}$/,
                                message:
                                  "Enter a valid 12-digit Aadhaar number",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter Aadhar Card Number"
                              type="text"
                              maxLength={12}
                            />
                          </Form.Item>
                        </Col>
                      ) : (
                        <></>
                      )}
                      <Col xs={22} sm={22} md={11} lg={11} xl={7}>
                        {" "}
                      </Col>
                    </Row> */}
                  </>
                )}
            </Form>
            {props?.schemeSelected &&
            props?.schemeSelected?.length > 0 &&
            (props?.schemeSelected?.toLowerCase()?.includes("cghs") ||
              props?.schemeSelected?.toLowerCase()?.includes("echs")) &&
            // props?.documentType === "CGHS_REFERRAL_LETTER" && documentTypeLabelName === "REFERRAL LETTER" && props?.isReferralUploaded ? (
            props?.isReferralUploaded &&
            !selectedCaseType?.toLowerCase()?.includes("emergency") ? (
              <PatientReferral
                form={form}
                showCompareModal={props?.showCompareModal}
                listOfResponseUrl={props?.listOfResponseUrl}
                showServices={props?.showServices}
                handleShowServices={props?.handleShowServices}
                fetchReferralDetail={referralData}
                setServices={setServices}
                setServicesManually={setServicesManually}
                services={services}
                servicesManually={servicesManually}
                setPatientReferralForm={setPatientReferralForm}
                schemeSelected={props?.schemeSelected}
                isDisabled={props?.isDisabled}
              />
            ) : (
              <></>
            )}
            {/* <AddPatientVisitModal selectedVisit={null} availableDoctor={props?.availableDoctor}/> */}
            {props?.schemeSelected &&
              // form.getFieldValue("uhidNo") &&
              form.getFieldValue("fullName") && (
                <Row
                  justify="center"
                  style={{ marginTop: "0.5rem", width: "100%" }}
                >
                  <Form.Item
                    wrapperCol={{ span: 24 }}
                    style={{ margin: 0, width: "100%" }}
                  >
                    {
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={props?.isPostPatientDetails}
                        onClick={handleSubmit}
                        className="form-button"
                        style={{
                          width: "100%",
                          // maxWidth: "200px",
                          height: "44px",
                          fontSize: "0.95rem",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                          borderRadius: "8px",
                          color: "white",
                        }}
                      >
                        {props?.schemeSelected?.toLowerCase()?.includes("echs")
                          ? "SAVE & GENERATE CLAIM ID"
                          : "REGISTER"}
                      </Button>
                    }
                  </Form.Item>
                </Row>
              )}
          </div>
        </div>
        {props?.isConfirmationModal && (
          <PatientConfirmationModal
            handleCancel={props?.handleCancel}
            handleConfirm={props?.handleConfirm}
            open={props?.open}
          />
        )}
        <ConfirmModal
          flagForPatientReg={flagForPatientReg}
          isModalOpen={props?.isModalOpen}
          setIsModalOpen={props?.setIsModalOpen}
          handleOk={props?.handleOk}
          handleCancel={props?.handleCancelConfirmationModal}
          isReferralLoading={props?.isReferralLoading}
          message={`ARE YOU SURE YOU WANT TO REGISTER PATIENT AND GENERATE ${scheme?.selectedScheme?.toLowerCase()?.includes("cghs") ? "Registration" : scheme?.selectedScheme?.toLowerCase()?.includes("capf") ? "Registration" : "Claim"} ID`}
          patientFormData={patientFormData}
          cardReferralError={cardReferralError}
        />
        <ConfirmModalUploadDoc
          isModalOpen={props?.isConfirmCardModal}
          setIsModalOpen={props?.setIsConfirmCardModal}
          handleOk={props?.handleOkConfirmDoc}
          handleCancel={props?.handleCancelConfirmDoc}
          isReferralLoading={props?.isReferralLoading}
          uhidNo={form.getFieldValue("uhidNo")}
          schemeSelected={props?.schemeSelected}
          patientDocType={patientDocType}
        />

        <RightSidePreviewModal
          open={previewDocument}
          handleCancel={() => setPreviewDocument(false)}
          documentUrl={documentUrl}
          key={documentUrl}
        />

        <ReferralEditModal
          open={isEditReferral}
          onCancel={() => setIsEditReferral(false)}
          form={form}
          schemeSelected={props?.schemeSelected}
          showServices={props?.showServices}
          setServices={setServices}
          services={services}
          setPatientReferralForm={setPatientReferralForm}
          referral={referralData}
          //   onSubmit={(values) => {
          //     console.log("Updated Referral:", values);
          //     setEditModalOpen(false);
          //   }}
        />
      </Spin>
    </div>
  );
};

export default AddNewPatientForm;
